/**
 * The office lodge service (13-24): thin by design.
 *
 * `submitLodge(io, register, values, actor, opts)` = `buildLodge(register, values, actor, now, opts)` (13-19) → on
 * refusals, return them; otherwise `getWriter().lodgeRecord(io, register, lodge, actor)` (13-23), which creates the
 * record and its `StateChange` sibling under the composed `ParentRef` (ISS-008) in live mode, and writes nothing in
 * practice mode (D-13-L-9). The 13-16 stop-gap payload and its hand-built field bag are gone.
 *
 * ---------------------------------------------------------------------------
 * AUTHORITY FLOOR (FIELDSET §1.3; D-13-L-1). Enforced in code, three times over.
 * ---------------------------------------------------------------------------
 * A lodge writes only its FIELDSET §3.2 columns, plus the two constants `Status` = `New` and `ClosureStatus` = `Open`
 * and its one `StateChange` sibling row. It never writes a §1.3 floor column on any register: no triage rating, no
 * reportability or SIRS answer, no notification, approval or closure column. Those are human acts by the verb that
 * owns them, each under that person's own identity with its own sibling row (D-13-L-1).
 *   1. `buildLodge` assembles the payload only from `lodgeColumns(register)` plus the constants, and refuses a floor
 *      column sent in `values` (`floor-column`) rather than dropping it silently (D-13-19-3).
 *   2. `lodgeRecord` (live and practice) re-checks the payload with `assertLodgeFields` and runs the `lodge` gate
 *      (`canDoRecordVerb`, D-13-23-3) before anything is sent.
 *   3. This service adds nothing to the payload: what the builder returns is what the writer gets.
 * ---------------------------------------------------------------------------
 *
 * Warnings (a phone number or email in a narrative, the repeated daylight-saving hour) never block a lodge, but
 * they are shown first: the person lodges "as it is" by passing `acceptWarnings` (D-13-19-4, D-13-24-6).
 */

import { buildLodge } from '../lib/lodgeBuilder';
import type { LodgeOptions, LodgeValues } from '../lib/lodgeBuilder';
import { RECORD_AUTHORITY_TEXT } from '../lib/recordAuthority';
import { LIVE_REF_PATTERN } from '../lib/recordQuery';
import { REGISTERS } from '../config/registers';
import { SHAREPOINT_SITE_DISPLAY_URL } from '../config/sharepoint';
import { getWriter } from './commitAdapter';
import type { Writer, WriteMode } from './commitAdapter';
import { RecordRefusedError } from './recordService';
import type { RecordIO } from './recordService';
import type { Actor } from '../types/actionRegister';
import type { RecordRegister, Refusal, SiblingDraft } from '../types/records';

export type SubmitOutcome =
  /** Nothing was sent. Show each refusal at its field and in the summary. */
  | { kind: 'refused'; refusals: Refusal[]; warnings: Refusal[] }
  /** The lodge would succeed, but it carries warnings the person has not seen yet. Nothing was sent. */
  | { kind: 'confirm'; warnings: Refusal[] }
  /** Lodged (or, in practice, logged). `auditPending` rows are owed and will be posted (D-13-L-5): never "done". */
  | {
      kind: 'lodged';
      register: RecordRegister;
      ref: string;
      mode: WriteMode;
      auditPending: SiblingDraft[];
      warnings: Refusal[];
      /** The SharePoint item's edit form (attachments are added there, D-13-L-6). `null` for a practice ref. */
      editFormUrl: string | null;
    };

export interface SubmitOptions extends Omit<LodgeOptions, 'auditId'> {
  /** Default `new Date()`. */
  now?: Date;
  /** The `[a:<id>]` tag (D-13-L-5). Default `crypto.randomUUID()`. */
  auditId?: string;
  /** The person has seen the warnings and chose to lodge as it is. */
  acceptWarnings?: boolean;
  /** The form's own refusals (`lodgeLayout.formRefusals`); any one stops the lodge. */
  formRefusals?: readonly Refusal[];
  /** Default `getWriter()` (practice unless `VITE_QMS_LIVE_WRITES === 'true'`). Pages pass the runtime's. */
  writer?: Writer;
}

/** The SharePoint edit form of a live record (`…/Lists/<title>/EditForm.aspx?ID=<id>`), else `null`. */
export function recordEditFormUrl(register: RecordRegister, ref: string): string | null {
  const m = LIVE_REF_PATTERN.exec(ref);
  if (!m) return null;
  const id = Number(m[2]);
  return `${SHAREPOINT_SITE_DISPLAY_URL}/Lists/${encodeURIComponent(REGISTERS[register].listTitle)}/EditForm.aspx?ID=${id}`;
}

/** Build the lodge and, when nothing refuses it and every warning has been seen, send it through the Writer. */
export async function submitLodge(
  io: RecordIO,
  register: RecordRegister,
  values: LodgeValues,
  actor: Actor,
  opts: SubmitOptions = {},
): Promise<SubmitOutcome> {
  const now = opts.now ?? new Date();
  const auditId = opts.auditId ?? crypto.randomUUID();
  const build = buildLodge(register, values, { upn: actor.upn, ...(actor.lookupId ? { siteUserId: actor.lookupId } : {}) }, now, {
    auditId,
    ...(opts.clientUnlisted ? { clientUnlisted: true } : {}),
    ...(opts.reportedOnDefaulted ? { reportedOnDefaulted: true } : {}),
  });
  const refusals = [...(opts.formRefusals ?? []), ...(build.ok ? [] : build.refusals)];
  if (refusals.length > 0 || !build.ok) return { kind: 'refused', refusals, warnings: build.warnings };
  if (build.warnings.length > 0 && !opts.acceptWarnings) return { kind: 'confirm', warnings: build.warnings };

  const writer = opts.writer ?? getWriter();
  try {
    const r = await writer.lodgeRecord(io, register, { fields: build.fields, siblings: build.siblings }, actor);
    return {
      kind: 'lodged',
      register,
      ref: r.ref,
      mode: writer.mode,
      auditPending: r.auditPending,
      warnings: build.warnings,
      editFormUrl: recordEditFormUrl(register, r.ref),
    };
  } catch (err) {
    if (err instanceof RecordRefusedError) {
      return {
        kind: 'refused',
        refusals: [{ code: err.reason, message: RECORD_AUTHORITY_TEXT[err.reason] }],
        warnings: build.warnings,
      };
    }
    throw err;
  }
}
